import React from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/svg/shared/ic-shared-right-arrow.svg';
import { CUSTOM_MENU_COMPONENTS } from '../../pages/BillPay/features/BillsHub/components/BillsModalsHub/consts';
import { BillPaymentReminder } from '../../pages/BillPay/features/BillsHub/components/BillMenuCustomItems/BillPaymentReminder';
import { featureAccess, LimitedFeatureType } from '../../services/appAccess.service';
import { FeatureAccess } from '../../pages/AccountSettings/components/rolesAndPermissions/PermissionsList';
import { FEATURE_FLAG } from '../../constants/general';

interface MenuItem {
  title?: string;
  name: string;
  SVGComponent?: any;
  SVGColor?: string;
  textColor?: string;
  arrowColor?: string;
  testId: string;
  isCrudOperation?: boolean;
  component?: string;
}

interface IPopoverMenu {
  menuItems: MenuItem[];
  onMenuItemClick: (name: string) => void;
  className?: string;
  testId: string;
}

const arrowStyles = 'before:content-none before:w-0 before:h-0 before:border-b-[20px] before:border-x-[20px]';

const ListMenu = ({ menuItems, onMenuItemClick, className, testId }: IPopoverMenu) => {
  const { t } = useTranslation();
  if (!menuItems || menuItems.length === 0) return null;
  const renderSeparator = () => (
    <div className='h-[1px] bg-bw03 my-[4px]' key={`MenuItem-separator-${Math.random()}`} />
  );
  const renderIcon = (SVGComponent: any, SVGColor: string | undefined) => {
    const strokeColor = SVGColor && `${SVGColor}`;
    return SVGComponent && <SVGComponent className={`${strokeColor}`} />;
  };
  const onClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, name: string): void => {
    e.stopPropagation();
    if (onMenuItemClick) onMenuItemClick(name);
  };
  const renderMenuItem = (menuItem: MenuItem) => {
    const billPayPermission = featureAccess(FEATURE_FLAG.BILL_PAY, LimitedFeatureType.BillPay);

    const shouldAvoidCrud = menuItem.isCrudOperation && billPayPermission !== FeatureAccess.READ_WRITE;
    if (!menuItem.name) return shouldAvoidCrud ? null : renderSeparator();
    const { title, SVGComponent, SVGColor, textColor, name, arrowColor, testId: actionTestId, component } = menuItem;
    if (component) {
      switch (component) {
        case CUSTOM_MENU_COMPONENTS.REMINDER:
          return <BillPaymentReminder externalDisabled={billPayPermission !== FeatureAccess.READ_WRITE} />;
        default:
          return null;
      }
    } else {
      return (
        <button
          disabled={shouldAvoidCrud}
          data-testid={`${actionTestId}`}
          type='button'
          onClick={(e) => onClick(e, name)}
          className={twMerge(
            'w-[calc(100%-8px)] mx-[4px] z-50 flex items-center pl-[12px] pr-[12px] h-[40px] group rounded-sm ',
            shouldAvoidCrud ? 'cursor-auto' : 'cursor-pinter',
          )}
          key={`MenuItem-${name}`}
        >
          {renderIcon(SVGComponent, shouldAvoidCrud ? 'stroke-text-main-disabled text-text-main-disabled' : SVGColor)}
          {title && (
            <div
              data-testid={`list-menu__button-${name}__title`}
              className={twMerge(
                'text-md font-metricR ml-[9px] text-left',
                textColor || '',
                shouldAvoidCrud ? 'text-text-main-disabled' : textColor || 'text-text-main-primary',
              )}
            >
              {t(title, title)}
            </div>
          )}
          {!shouldAvoidCrud && (
            <ArrowRightIcon
              className={`w-[16px] h-[16px] ml-[auto] opacity-0 group-hover:opacity-100 ${arrowColor || 'text-bw07'}`}
            />
          )}
        </button>
      );
    }
  };
  const itemsToRender = menuItems.map(renderMenuItem);
  return (
    <div
      data-testid={testId}
      className={`rounded bg-bw00 z-[100] relative py-[4px] box-border ${className ?? ''} ${arrowStyles} `}
    >
      {itemsToRender}
    </div>
  );
};

export default ListMenu;
